import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Share2, Copy, Check, Youtube, Facebook, Instagram, Twitter, Music2, 
  Upload, Sparkles, ExternalLink, Clock, CheckCircle, FileAudio, FileVideo,
  Hash, MessageSquare, Loader2, Plus, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social Media Platform Config
const PLATFORMS = [
  { 
    id: 'youtube', 
    name: 'YouTube', 
    icon: Youtube, 
    color: '#FF0000', 
    bgColor: '#FF000015',
    postUrl: 'https://studio.youtube.com/channel/UCZ8ewM6AKFbobbU6TscphoA/videos/upload',
    charLimit: 5000,
    hashtagStyle: 'spaced'
  },
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: Facebook, 
    color: '#1877F2', 
    bgColor: '#1877F215',
    postUrl: 'https://www.facebook.com/',
    charLimit: 63206,
    hashtagStyle: 'spaced'
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: Instagram, 
    color: '#E4405F', 
    bgColor: '#E4405F15',
    postUrl: 'https://www.instagram.com/',
    charLimit: 2200,
    hashtagStyle: 'grouped'
  },
  { 
    id: 'tiktok', 
    name: 'TikTok', 
    icon: Music2, 
    color: '#000000', 
    bgColor: '#00000010',
    postUrl: 'https://www.tiktok.com/upload',
    charLimit: 2200,
    hashtagStyle: 'grouped'
  },
  { 
    id: 'twitter', 
    name: 'X (Twitter)', 
    icon: Twitter, 
    color: '#1DA1F2', 
    bgColor: '#1DA1F215',
    postUrl: 'https://twitter.com/compose/tweet',
    charLimit: 280,
    hashtagStyle: 'inline'
  },
  { 
    id: 'spotify', 
    name: 'Spotify', 
    icon: Music2, 
    color: '#1DB954', 
    bgColor: '#1DB95415',
    postUrl: 'https://podcasters.spotify.com/',
    charLimit: 1000,
    hashtagStyle: 'none'
  }
];

// Default hashtags for ministry content
const DEFAULT_HASHTAGS = [
  'LivingFruitMinistry', 'Faith', 'Church', 'Sermon', 'Bible', 
  'Christian', 'Worship', 'Prayer', 'Gospel', 'Jesus', 
  'Kissimmee', 'Florida', 'CentralFlorida', 'ChurchFamily'
];

export default function AdminContentHub() {
  const [content, setContent] = useState({
    title: '',
    description: '',
    type: 'sermon', // sermon, podcast, announcement, event
    mediaUrl: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  const [selectedHashtags, setSelectedHashtags] = useState(['LivingFruitMinistry', 'Faith', 'Sermon', 'Church']);
  const [customHashtag, setCustomHashtag] = useState('');
  const [generatedCaptions, setGeneratedCaptions] = useState({});
  const [copiedPlatform, setCopiedPlatform] = useState(null);
  const [postedPlatforms, setPostedPlatforms] = useState([]);
  const [savedContent, setSavedContent] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Load saved content from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('lfm_content_hub_history');
    if (saved) {
      setSavedContent(JSON.parse(saved));
    }
  }, []);

  // Generate captions for all platforms
  const generateCaptions = () => {
    if (!content.title) {
      toast.error('Please enter a title first');
      return;
    }

    setGenerating(true);
    
    setTimeout(() => {
      const hashtags = selectedHashtags.map(h => `#${h}`);
      const hashtagString = hashtags.join(' ');
      const groupedHashtags = '\n\n' + hashtags.join(' ');
      
      const captions = {};
      
      // YouTube - Long form description
      captions.youtube = `${content.title}

${content.description || 'Join us for this powerful message from Living Fruit Ministry!'}

🌳 Welcome to Living Fruit Ministry!
📍 109 N Beaumont Avenue #1078, Kissimmee, FL 34741
🕐 Sunday Worship: 10:00 AM

${hashtagString}

Subscribe and hit the bell 🔔 to never miss a message!

Website: https://livingfruitministry.org
Facebook: https://facebook.com/livingfruitministry
Instagram: https://instagram.com/livingfruitministry`;

      // Facebook - Engaging post
      captions.facebook = `🌳 ${content.title}

${content.description || 'Join us for this powerful message!'}

${content.type === 'sermon' ? '🎬 Watch the full sermon on our YouTube channel!' : '🎧 Listen now on Spotify!'}

${hashtagString}

💚 Share this message with someone who needs to hear it today!`;

      // Instagram - Visual focused
      captions.instagram = `🌳 ${content.title}

${content.description || 'A message of hope and faith from Living Fruit Ministry.'}

${content.type === 'sermon' ? '🎬 Full sermon available - link in bio!' : '🎧 Full episode on Spotify - link in bio!'}

Double tap if this blessed you! 💚
${groupedHashtags}`;

      // TikTok - Short & catchy
      captions.tiktok = `${content.title} 🌳

${content.description ? content.description.substring(0, 100) + '...' : 'This message will change your perspective!'}

Full ${content.type} on YouTube/Spotify 🔗
${groupedHashtags}`;

      // Twitter/X - Concise
      const shortDesc = content.description ? content.description.substring(0, 100) : 'New message available!';
      captions.twitter = `🌳 ${content.title}

${shortDesc}

${hashtags.slice(0, 4).join(' ')}`;

      // Spotify - Episode description
      captions.spotify = `${content.title}

${content.description || 'Join us for this message from Living Fruit Ministry in Kissimmee, Florida.'}

Visit us: livingfruitministry.org
Sunday Worship: 10:00 AM

Share this episode with someone who needs encouragement today!`;

      setGeneratedCaptions(captions);
      setGenerating(false);
      toast.success('Captions generated for all platforms!');
    }, 500);
  };

  // Copy caption to clipboard
  const copyCaption = (platformId) => {
    const caption = generatedCaptions[platformId];
    if (caption) {
      navigator.clipboard.writeText(caption);
      setCopiedPlatform(platformId);
      toast.success(`Copied ${PLATFORMS.find(p => p.id === platformId)?.name} caption!`);
      setTimeout(() => setCopiedPlatform(null), 2000);
    }
  };

  // Mark platform as posted
  const markAsPosted = (platformId) => {
    if (postedPlatforms.includes(platformId)) {
      setPostedPlatforms(postedPlatforms.filter(p => p !== platformId));
    } else {
      setPostedPlatforms([...postedPlatforms, platformId]);
    }
  };

  // Save content to history
  const saveToHistory = () => {
    const newEntry = {
      id: Date.now(),
      ...content,
      hashtags: selectedHashtags,
      postedTo: postedPlatforms,
      createdAt: new Date().toISOString()
    };
    
    const updated = [newEntry, ...savedContent].slice(0, 50); // Keep last 50
    setSavedContent(updated);
    localStorage.setItem('lfm_content_hub_history', JSON.stringify(updated));
    toast.success('Saved to history!');
  };

  // Clear form
  const clearForm = () => {
    setContent({
      title: '',
      description: '',
      type: 'sermon',
      mediaUrl: '',
      date: new Date().toISOString().split('T')[0]
    });
    setGeneratedCaptions({});
    setPostedPlatforms([]);
  };

  // Add custom hashtag
  const addCustomHashtag = () => {
    if (customHashtag && !selectedHashtags.includes(customHashtag)) {
      setSelectedHashtags([...selectedHashtags, customHashtag.replace(/^#/, '')]);
      setCustomHashtag('');
    }
  };

  // Toggle hashtag
  const toggleHashtag = (tag) => {
    if (selectedHashtags.includes(tag)) {
      setSelectedHashtags(selectedHashtags.filter(t => t !== tag));
    } else {
      setSelectedHashtags([...selectedHashtags, tag]);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-content-hub">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif text-charcoal flex items-center gap-3">
            <Share2 className="text-burgundy" />
            Content Hub
          </h1>
          <p className="text-charcoal/60 text-sm mt-1">
            Create once, share everywhere
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors text-sm flex items-center gap-2"
          >
            <Clock size={16} />
            History
          </button>
          <button
            onClick={clearForm}
            className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Content Input */}
        <div className="space-y-6">
          {/* Content Type */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="font-medium text-charcoal mb-4 flex items-center gap-2">
              <FileAudio size={18} className="text-gold" />
              Content Details
            </h2>
            
            {/* Type Selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['sermon', 'podcast', 'announcement', 'event'].map(type => (
                <button
                  key={type}
                  onClick={() => setContent({...content, type})}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    content.type === type
                      ? 'bg-burgundy text-white'
                      : 'bg-cream text-charcoal hover:bg-gold/20'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-charcoal">
                Title *
              </label>
              <input
                type="text"
                value={content.title}
                onChange={(e) => setContent({...content, title: e.target.value})}
                placeholder="e.g., Walking in Faith - Sunday Service"
                className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                data-testid="content-title"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-charcoal">
                Description
              </label>
              <textarea
                rows={4}
                value={content.description}
                onChange={(e) => setContent({...content, description: e.target.value})}
                placeholder="Brief description of the message..."
                className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                data-testid="content-description"
              />
            </div>

            {/* Media URL (optional) */}
            <div>
              <label className="block text-sm font-medium mb-2 text-charcoal">
                Media Link (optional)
              </label>
              <input
                type="url"
                value={content.mediaUrl}
                onChange={(e) => setContent({...content, mediaUrl: e.target.value})}
                placeholder="YouTube or Spotify link..."
                className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
              />
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="font-medium text-charcoal mb-4 flex items-center gap-2">
              <Hash size={18} className="text-gold" />
              Hashtags
            </h2>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {DEFAULT_HASHTAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleHashtag(tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedHashtags.includes(tag)
                      ? 'bg-burgundy text-white'
                      : 'bg-cream text-charcoal hover:bg-gold/20'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={customHashtag}
                onChange={(e) => setCustomHashtag(e.target.value.replace(/\s/g, ''))}
                onKeyPress={(e) => e.key === 'Enter' && addCustomHashtag()}
                placeholder="Add custom hashtag..."
                className="flex-1 px-3 py-2 rounded-lg border border-gold/20 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
              />
              <button
                onClick={addCustomHashtag}
                className="px-3 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors"
              >
                <Plus size={18} />
              </button>
            </div>

            <p className="text-xs text-charcoal/50 mt-3">
              Selected: {selectedHashtags.length} hashtags
            </p>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateCaptions}
            disabled={generating || !content.title}
            className="w-full py-4 rounded-xl bg-burgundy hover:bg-purple text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="generate-captions-btn"
          >
            {generating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Generate Captions for All Platforms
              </>
            )}
          </button>
        </div>

        {/* Right Column - Generated Captions */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-charcoal flex items-center gap-2">
              <MessageSquare size={18} className="text-gold" />
              Generated Captions
            </h2>
            {Object.keys(generatedCaptions).length > 0 && (
              <button
                onClick={saveToHistory}
                className="text-sm text-burgundy hover:text-purple flex items-center gap-1"
              >
                <CheckCircle size={14} />
                Save to History
              </button>
            )}
          </div>

          {Object.keys(generatedCaptions).length === 0 ? (
            <div className="bg-cream/50 rounded-2xl p-12 text-center">
              <Share2 size={48} className="mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50">
                Fill in your content details and click "Generate" to create captions for all platforms
              </p>
            </div>
          ) : (
            PLATFORMS.map(platform => {
              const Icon = platform.icon;
              const isPosted = postedPlatforms.includes(platform.id);
              const isCopied = copiedPlatform === platform.id;
              
              return (
                <motion.div
                  key={platform.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-xl border p-4 ${
                    isPosted ? 'border-green-300 bg-green-50/30' : 'border-gold/10'
                  }`}
                >
                  {/* Platform Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: platform.bgColor }}
                      >
                        <Icon size={18} style={{ color: platform.color }} />
                      </div>
                      <span className="font-medium text-charcoal">{platform.name}</span>
                      {isPosted && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Posted ✓
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyCaption(platform.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isCopied ? 'bg-green-100 text-green-600' : 'hover:bg-cream'
                        }`}
                        title="Copy caption"
                      >
                        {isCopied ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                      <a
                        href={platform.postUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-cream transition-colors"
                        title={`Open ${platform.name}`}
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button
                        onClick={() => markAsPosted(platform.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isPosted ? 'bg-green-100 text-green-600' : 'hover:bg-cream'
                        }`}
                        title="Mark as posted"
                      >
                        <CheckCircle size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Caption Preview */}
                  <div className="bg-cream/50 rounded-lg p-3 text-sm text-charcoal/80 max-h-32 overflow-y-auto whitespace-pre-wrap">
                    {generatedCaptions[platform.id]}
                  </div>

                  {/* Character Count */}
                  <div className="mt-2 flex justify-between text-xs text-charcoal/50">
                    <span>{generatedCaptions[platform.id]?.length || 0} / {platform.charLimit} characters</span>
                    {generatedCaptions[platform.id]?.length > platform.charLimit && (
                      <span className="text-red-500">Over limit!</span>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}

          {/* Progress */}
          {Object.keys(generatedCaptions).length > 0 && (
            <div className="bg-cream rounded-xl p-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-charcoal">Posting Progress</span>
                <span className="text-sm text-charcoal/60">
                  {postedPlatforms.length} / {PLATFORMS.length}
                </span>
              </div>
              <div className="h-2 bg-white rounded-full overflow-hidden">
                <div 
                  className="h-full bg-burgundy transition-all duration-300"
                  style={{ width: `${(postedPlatforms.length / PLATFORMS.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 bg-white rounded-2xl border border-gold/10 p-6"
        >
          <h2 className="font-medium text-charcoal mb-4 flex items-center gap-2">
            <Clock size={18} className="text-gold" />
            Content History
          </h2>
          
          {savedContent.length === 0 ? (
            <p className="text-charcoal/50 text-center py-8">No saved content yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {savedContent.map(item => (
                <div 
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-cream/50 rounded-xl"
                >
                  <div>
                    <h4 className="font-medium text-charcoal">{item.title}</h4>
                    <div className="flex items-center gap-2 mt-1 text-xs text-charcoal/50">
                      <span className="px-2 py-0.5 bg-white rounded">{item.type}</span>
                      <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                      <span>• Posted to {item.postedTo?.length || 0} platforms</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setContent({
                        title: item.title,
                        description: item.description,
                        type: item.type,
                        mediaUrl: item.mediaUrl || '',
                        date: item.date
                      });
                      setSelectedHashtags(item.hashtags || []);
                      setShowHistory(false);
                      toast.success('Loaded from history');
                    }}
                    className="px-3 py-1.5 rounded-lg bg-white hover:bg-gold/20 text-sm transition-colors"
                  >
                    Load
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Quick Links to Social Platforms */}
      <div className="mt-8 bg-gradient-to-r from-burgundy to-purple rounded-2xl p-6 text-white">
        <h3 className="font-medium mb-4">Quick Post Links</h3>
        <div className="flex flex-wrap gap-3">
          {PLATFORMS.map(platform => {
            const Icon = platform.icon;
            return (
              <a
                key={platform.id}
                href={platform.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Icon size={18} />
                <span className="text-sm">{platform.name}</span>
                <ExternalLink size={14} className="opacity-50" />
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
